import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, Download, FileText, Compass } from "lucide-react";

export default function MessyRoomsToolPage() {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Messy Rooms &amp; Growing Independence | ALIGN&bull;</title>
        <meta
          name="description"
          content="Free parent guide and companion tracker from ALIGN•. Reframe the messy room conversation and support your teenager's growing autonomy."
        />
      </Helmet>

      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#F7F5F0" }} data-testid="messy-rooms-tool-page">
        {/* Header */}
        <header className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-40">
          <div className="max-w-3xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
            <button
              onClick={() => navigate("/")}
              className="font-['Cormorant_Garamond'] text-2xl font-light tracking-tight text-[#1A1C19] hover:text-[#4A6741] transition-colors"
            >
              ALIGN<span className="text-[#4A6741]">&bull;</span>
            </button>
            <button
              onClick={() => navigate("/")}
              className="text-xs text-[#5C635A] hover:text-[#4A6741] transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft size={12} />
              Back to site
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 flex items-center justify-center px-6 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-xl"
          >
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-4">
                Free Resource
              </p>
              <h1 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light tracking-tight text-[#1A1C19] mb-4 leading-[1.15]">
                Messy Rooms &amp;
                <br />
                <span className="italic text-[#4A6741]">Growing Independence</span>
              </h1>
              <p className="text-base font-light text-[#5C635A] leading-relaxed max-w-md mx-auto">
                A research-backed guide that reframes the messy room conversation,
                plus a printable companion tracker your teenager can use to build
                their own routines.
              </p>
            </div>

            {/* Download Cards */}
            <div className="space-y-4 mb-10">
              <a
                href="https://customer-assets.emergentagent.com/job_wellness-journey-231/artifacts/btgcesnl_Messy-Rooms-and-Growing-Independence-Align-Praxis%20%281%29.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="glass-card p-6 flex items-center gap-5 group hover:border-[#4A6741]/20"
                data-testid="tool-download-guide"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#4A6741]/10 flex items-center justify-center shrink-0 group-hover:bg-[#4A6741]/15 transition-colors">
                  <FileText size={20} className="text-[#4A6741]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#4A6741] mb-0.5">
                    Parent Guide
                  </p>
                  <h2 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1A1C19]">
                    Messy Rooms &amp; Growing Independence
                  </h2>
                  <p className="text-xs font-light text-[#5C635A] mt-1">
                    Understand the why — five research-backed strategies
                  </p>
                </div>
                <Download size={18} className="text-[#4A6741] shrink-0 group-hover:translate-y-0.5 transition-transform" />
              </a>

              <a
                href="https://customer-assets.emergentagent.com/job_wellness-journey-231/artifacts/g9t40sml_Personal-Space-Goal-Tracker-Align-Praxis.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="glass-card p-6 flex items-center gap-5 group hover:border-[#4A6741]/20"
                data-testid="tool-download-tracker"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#4A6741]/10 flex items-center justify-center shrink-0 group-hover:bg-[#4A6741]/15 transition-colors">
                  <Compass size={20} className="text-[#4A6741]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#4A6741] mb-0.5">
                    Companion Tracker
                  </p>
                  <h2 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1A1C19]">
                    Personal Space Goal Tracker
                  </h2>
                  <p className="text-xs font-light text-[#5C635A] mt-1">
                    Printable tool for your teenager to set goals and track progress
                  </p>
                </div>
                <Download size={18} className="text-[#4A6741] shrink-0 group-hover:translate-y-0.5 transition-transform" />
              </a>
            </div>

            {/* CTA */}
            <div className="text-center">
              <p className="text-xs font-light text-[#5C635A] mb-4">
                Want to know more about how ALIGN&bull; supports families?
              </p>
              <button
                onClick={() => navigate("/")}
                className="btn-pill text-[11px]"
              >
                Visit ALIGN&bull;
              </button>
            </div>
          </motion.div>
        </main>

        {/* Minimal Footer */}
        <footer className="text-center py-6 px-6">
          <p className="text-xs font-light text-[#5C635A]/50">
            &copy; {new Date().getFullYear()} ALIGN&bull; Integration Psychology Practice &middot; Perth
          </p>
        </footer>
      </div>
    </>
  );
}
